import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './styles/markdown.css';
import { SessionProvider } from './context/SessionContext';
import { AuthProvider } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { LocalizationProvider } from './context/LocalizationContext';
import { PromptSuggestionsProvider } from './context/PromptSuggestionsContext';
import { UserPreferencesProvider } from './context/UserPreferencesContext';
import { AuthGuard } from './components/AuthGuard';
import { ChatLayout } from './components/ChatLayout';
import { LocaleReadyGate } from './components/LocaleReadyGate';
import { ThemedSpinner } from './components/ThemedSpinner';
import { useSession } from './context/SessionContext';
import { useTranslation } from './localization/useAppTranslation';

const LazyAdminDashboard = React.lazy(() =>
  import('./components/AdminDashboard').then((module) => ({ default: module.AdminDashboard }))
);

// Route guard for admin access
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session } = useSession();
  
  if (!session?.isSuperuser && !session?.permissions?.['speedbot.access_admin_dashboard']) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

function AppRoutes() {
  const { t } = useTranslation('admin');

  return (
    <Routes>
      <Route path="/" element={<ChatLayout />} />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <React.Suspense
              fallback={
                <main className="App-main">
                  <ThemedSpinner size="lg" ariaLabel={t('status.loadingData')} />
                </main>
              }
            >
              <LazyAdminDashboard />
            </React.Suspense>
          </AdminRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SessionProvider>
            <UserPreferencesProvider>
              <LocalizationProvider>
                <PromptSuggestionsProvider>
                  <ChatProvider>
                    <AuthGuard>
                      <LocaleReadyGate>
                        <AppRoutes />
                      </LocaleReadyGate>
                    </AuthGuard>
                  </ChatProvider>
                </PromptSuggestionsProvider>
              </LocalizationProvider>
            </UserPreferencesProvider>
          </SessionProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;

